import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Checkbox, Divider, Grid, InputAdornment, List, ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, MenuItem, Paper } from '@mui/material';
import { Add, Check, Delete, Email, EmailOutlined, UndoOutlined } from "@mui/icons-material";
import { isEqual } from "lodash";
import { NOTIFICATION_TYPE, showNotificationBar } from "../../../actions/error-pages/NotificationBarActions";
import { getUserId } from "../../../actions/profile/UserActions";
import { createNewSiteSubscription, getProducts, updateSiteSubscription } from "../../../clients/bettercallkyle/BckApi";
import Colors from "../../../constants/Colors";
import { getSiteByIdForUser, getSiteSubscription } from "../../../actions/internal/SiteActions";
import StyledSubHeader from "../../common/StyledSubHeader";
import { getSiteById } from "../../../selectors/SiteSelectors";
import StyledProgressCircle from "../../common/StyledProgressCircle";
import { StyledTextField } from "../../common/StyledTextField";
import StyledAccordion from "../../common/StyledAccordion";
import { ContainedButton, LoadingButton, TextButton, TooltipIconButton } from "../../common/StyledButtons";
import { SUMMARY_TITLE_FONT } from "../../../constants/Styles";
import { formatDateToFancyString } from "../../../utilities/DateUtilities";
import StyledBanner, { BANNER_VARIANTS } from "../../common/StyledBanner";
import StyledPage from "../../common/StyledPage";
import StyledSection from "../../common/StyledSection";
import StyledSwitch from "../../common/StyledSwitch";

const AdminEditSubscriptionPage = ({ match, history }) => {

    const subscription = useRef();
    const customer = useRef();
    const products = useRef();
    const stripeDetails = useRef();

    const plans = useRef([]);
    const emailPackages = useRef([]);
    const hostingPackages = useRef([]);
    const supportPlans = useRef([]);
    const originalFields = useRef();

    const [ isLoading, setIsLoading ] = useState(true);
    const [ errors, setErrors ] = useState([]);
    const [ fields, setFields ] = useState({});
    const [ emailToAdd, setEmailToAdd ] = useState('');
    const [ sendNotification, setSendNotification ] = useState(false);

    const site = getSiteById(match.params.site_id);

    useEffect(() => {
        loadSession()
    }, [])

    const getSiteBusinessesEmails = (site) => {
        if (!site || !site.site_service_modules || !site.site_service_modules.email) { return [] }
        return site.site_service_modules.email.emails || [];
    }

    const getSelectedExtraServices = () => {
        if (!subscription.current) { return {} }
        const items = subscription.current.items.data.filter((item) => !item.product.metadata.group)
            .reduce((acc, val) => ({ ...acc, [val.price.id] : { quantity: val.quantity, product: val.product, price: val.price } }), {})
        return items;
    }

    const getSelectedAddOns = () => {
        if (!subscription.current) { return {} }
        const items = subscription.current.items.data.filter((item) => item.product.metadata.group === "ADDON")
            .reduce((acc, val) => ({ ...acc, [val.price.id] : { quantity: val.quantity, product: val.product, price: val.price } }), {})
        return items;
    }

    const getCurrentPlan = () => {
        if (!subscription.current) { return undefined }
        const item = subscription.current.items.data.find((item) => item.product && item.product.metadata.group === 'PLAN')
        if (!item) { return undefined; }
        return item;
    }

    const getCurrentInterval = () => {
        if (!subscription.current) { return undefined }
        const item = subscription.current.items.data.find((item) => item.product && item.product.metadata.group === 'PLAN')
        if (!item) { return undefined; }
        return item.price.recurring.interval;
    }
    
    const getHostingPackagePriceId = () => {
        if (!subscription.current) { return undefined }
        const item = subscription.current.items.data.find((item) => item.product && item.product.metadata.group === 'hosting')
        if (!item) { return undefined; }
        return item.price.id;
    }

    const getSupportPlanPriceId = () => {
        if (!subscription.current) { return null }
        const item = subscription.current.items.data.find((item) => item.product && item.product.metadata.group === 'support')
        if (!item) { return null; }
        return item.price.id;
    }

    const getEmailHostingPriceId = () => {
        if (!subscription.current) { return undefined }
        const item = subscription.current.items.data.find((item) => item.product && item.product.metadata.group === 'email')
        if (!item) { return undefined; }
        return item.price.id;
    }

    const getSubscriptionRenewalDate = () => {
        if (!subscription.current) {
            const date = new Date();
            const newDate = new Date(date.setMonth(date.getMonth() + 1))
            return formatDateToFancyString(newDate.getTime())
        } else {
            return formatDateToFancyString(subscription.current.current_period_end * 1000)
        }
    }

    const getTotalPriceForSubscription = () => {
        if (!subscription.current) return 0;
        return subscription.current.items.data.reduce((acc, val) => acc += val.price.unit_amount * val.quantity, 0);
    }

    const getPlanInterval = () => {
        if (subscription.current) { 
            const item = subscription.current.items.data[0];
            const interval = item && item.price && item.price.recurring && item.price.recurring.interval;
            if (interval) return interval;
        }
        if (plans.current !== undefined && fields.planSelection && fields.planPriceSelection) {
            const planId = fields.planSelection;
            const plan = plans.current.find(ep => ep.id === planId);    
            const planPrice = plan.prices.find(p => p.id === fields.planPriceSelection);
            const interval = planPrice && planPrice.recurring && planPrice.recurring.interval;
            if (interval) return interval;
        }
        return undefined;   
    }


    const filterPriceForInterval = (prices) => {
        if (!prices) return;
        const planInterval = getPlanInterval();
        const filtered = prices.filter(p => {
            return (p && p.recurring && p.recurring.interval === planInterval) || !planInterval
        });
        return filtered;
    }

    const newTotalPrice = useMemo(() => {
        let totalPrice = 0;
    
        const getSelectedItemsTotal = (selectedItems) => {
            if (!selectedItems || typeof selectedItems !== 'object') {
                return 0;
            }

            const selectedItemPricesArr = Object.values(selectedItems).map(item => ({ ...item.price, quantity: item.quantity }));

            const filtered = filterPriceForInterval(selectedItemPricesArr);
    
            const amt = filterPriceForInterval(selectedItemPricesArr).reduce((acc, val) => {
                if (val) {
                    return acc + (val.unit_amount * val.quantity);
                }
    
                return acc;
            }, 0);
            return amt;
        };
    
        if (fields.planPriceSelection) {
            const planId = fields.planSelection;
            const plan = plans.current.find(ep => ep.id === planId);
    
            let planPrice = null;
    
            if (plan && plan.prices) {
                planPrice = plan.prices.find(p => p.id === fields.planPriceSelection);
            }
    
            totalPrice += planPrice ? planPrice.unit_amount : 0;
        }
    
        if (fields.emailHostingPriceId) {
            const emails = fields.emails || [];
            const emailsToRemove = fields.emailsToRemove || [];
            const emailsToAdd = fields.emailsToAdd || [];
    
            const updatedEmails = emails.filter(e => !emailsToRemove.includes(e));
            updatedEmails.push(...emailsToAdd);
    
            const price = filterPriceForInterval(emailPackages.current)
                .find(ep => ep.id === fields.emailHostingPriceId);
    
            totalPrice += price ? price.unit_amount * updatedEmails.length : 0;
        }
    
        if (fields.hostingPackagePriceId) {
            const price = filterPriceForInterval(hostingPackages.current)
                .find(ep => ep.id === fields.hostingPackagePriceId);
    
            totalPrice += price ? price.unit_amount : 0;
        }
    
        if (fields.supportPlanPriceId) {
            const price = filterPriceForInterval(supportPlans.current)
                .find(ep => ep.id === fields.supportPlanPriceId);
    
            totalPrice += price ? price.unit_amount : 0;
        }
        
        totalPrice += getSelectedItemsTotal(fields.selectedExtraServices);
        totalPrice += getSelectedItemsTotal(fields.selectedAddOns);
    
        return totalPrice;
    }, [
        fields
    ]);


    const loadSession = async () => {
        try {
            const siteId = match.params.site_id
            const userId = getUserId()

            let fetchedSite;

            await Promise.all([
                getSiteByIdForUser(siteId, userId).then(resp => { fetchedSite = resp; }),
                getProducts().then(resp => { 
                    products.current = resp.products }),
                getSiteSubscription(userId, siteId).then(resp => {
                    if (resp.stripe_customer) customer.current = resp.stripe_customer;
                    if (resp.stripe_subscription) subscription.current = resp.stripe_subscription;
                    if (resp.stripe_details) stripeDetails.current = resp.stripe_details;
                })
            ])
            let hourly_rate;
            if (fetchedSite 
                && fetchedSite.site_service_modules 
                && fetchedSite.site_service_modules.consulting 
                && fetchedSite.site_service_modules.consulting.general_consulting) {
                hourly_rate = (fetchedSite.site_service_modules.consulting.general_consulting.pricing.default / 100).toFixed(2);
            }
            plans.current = products.current.filter((product) => product && product.metadata.group === 'PLAN');

            hostingPackages.current = products.current.find((product) => product && product.metadata.group === 'hosting').prices;
            hostingPackages.current = hostingPackages.current.sort((a, b) =>
                (a.nickname.includes('(LEGACY)') ? 1 : 0) - (b.nickname.includes('(LEGACY)') ? 1 : 0) ||
                a.unit_amount - b.unit_amount
            );

            const sortedSupportPlans = products.current.find((product) => product && product.metadata.group === 'support');
            if (sortedSupportPlans) {
                supportPlans.current = sortedSupportPlans.prices.sort((a,b) => a.unit_amount - b.unit_amount);
            }

            emailPackages.current = products.current.filter((product) => product && product.metadata.group === 'email').flatMap(product => product.prices);

            const plan = getCurrentPlan();
            const interval = getCurrentInterval();
            // console.log("NEED TO HOOK UP", interval, plan);

            const existingFields = {
                planSelection: plan && plan.product.id,
                planPriceSelection: plan && plan.price.id,
                planHourlyRate: hourly_rate,

                hostingPackagePriceId: getHostingPackagePriceId(),
                emailHostingPriceId: getEmailHostingPriceId(),
                supportPlanPriceId: getSupportPlanPriceId(),
                emails: getSiteBusinessesEmails(fetchedSite),
                selectedExtraServices: getSelectedExtraServices(),
                selectedAddOns: getSelectedAddOns(),
                emailsToRemove: [],
                emailsToAdd: [],
                totalPrice: getTotalPriceForSubscription()
            }

            originalFields.current = { ...existingFields };

            setFields(existingFields);
            setIsLoading(false)
        } catch (err) {
            const error = await err;
            showNotificationBar('error', `Uh oh! A problem occurred while loading data. Details: ${error.message || 'No additional details.'}`);
            history.goBack();
            console.error(err)
        }
    }

    const isFormValid = (fieldsToCheck) => {
        const foundErrors = [];
        if (fieldsToCheck.emailHostingPriceId) {
            const updatedEmails = fieldsToCheck.emails.filter(e => !fieldsToCheck.emailsToRemove.includes(e));
            updatedEmails.push(...fieldsToCheck.emailsToAdd)
            if (updatedEmails.length === 0) foundErrors.push('emailToAdd')
        }
        // Plan selection check
        if (fieldsToCheck.planSelection && !fieldsToCheck.planPriceSelection) {
            foundErrors.push('planPriceSelection');
        }
        else if (fieldsToCheck.planSelection) {
            const planId = fieldsToCheck.planSelection;
            const plan = plans.current.find(ep => ep.id === planId);
            const planPrice = plan.prices.find(p => p.id === fieldsToCheck.planPriceSelection);
            if (!planPrice) {
                foundErrors.push('planSelection');
                foundErrors.push('planPriceSelection');
            }
        }

        if (fieldsToCheck.planHourlyRate === undefined || fieldsToCheck.planHourlyRate < 0) foundErrors.push("planHourlyRate");
        setErrors(foundErrors)

        return foundErrors.length === 0;
    }

    const handleSaveSubscription = async () => {
        const interval = getPlanInterval();
        const updatedFields = validateOrRemoveImproperPricesForInterval(interval, fields);
        if (!isFormValid(updatedFields)) {
            showNotificationBar(NOTIFICATION_TYPE.WARNING, 'Some fields in this form are invalid. Please check above before continuing.')
            return;
        }

        const emailProduct = products.current.filter((product) => product && product.metadata.group === 'email')
        .find(product => product.prices.some(price =>  price.id === updatedFields.emailHostingPriceId));

        const updatedEmails = updatedFields.emails.filter(e => !updatedFields.emailsToRemove.includes(e));
        updatedEmails.push(...updatedFields.emailsToAdd)

        const email = updatedFields.emailHostingPriceId === undefined ? { } : {
            product_id: emailProduct.id,
            price_id: updatedFields.emailHostingPriceId,
            emails: updatedEmails
        }

        const hourlyRate = Math.round(Number(updatedFields.planHourlyRate || 0) * 100);

        const consulting = site.site_service_modules && site.site_service_modules.consulting
            ? { ...site.site_service_modules.consulting }
            : {};

        const generalConsulting = consulting.general_consulting
            ? { ...consulting.general_consulting }
            : {};

        const pricing = generalConsulting.pricing
            ? { ...generalConsulting.pricing }
            : {};

        pricing.default = hourlyRate;
        generalConsulting.pricing = pricing;
        consulting.general_consulting = generalConsulting;

        const site_service_modules = { ...site.site_service_modules, email, consulting };
        const site_subscription_products = Object.keys(updatedFields.selectedExtraServices).map(priceId => ({ price: priceId, quantity: updatedFields.selectedExtraServices[priceId].quantity }))
        const addon_products = Object.keys(updatedFields.selectedAddOns).map(priceId => ({ price: priceId, quantity: updatedFields.selectedAddOns[priceId].quantity }));
        if (addon_products && addon_products.length > 0) site_subscription_products.push(...addon_products);
        if (updatedFields.hostingPackagePriceId) site_subscription_products.push({ price: updatedFields.hostingPackagePriceId, quantity: 1 });
        if (updatedFields.supportPlanPriceId) site_subscription_products.push({ price: updatedFields.supportPlanPriceId, quantity: 1 });
        if (updatedFields.emailHostingPriceId) site_subscription_products.push({ price: updatedFields.emailHostingPriceId, quantity: updatedEmails.length });
        if (updatedFields.planPriceSelection) site_subscription_products.push({ price: updatedFields.planPriceSelection, quantity: 1 });

        try {
            setIsLoading(true)
            const options = { send_notification: sendNotification }
            const userId = getUserId();
            const siteId = site.site_id;
            let message;
            if (!subscription.current) {
                await createNewSiteSubscription(userId, siteId, site_service_modules, site_subscription_products, options)
                message = 'Subscription created successfully!';
            } else {
                await updateSiteSubscription(userId, siteId, site_service_modules, site_subscription_products, options)
                message = 'Subscription updated successfully!';
            }
            await Promise.all([
                getSiteByIdForUser(siteId, userId, true),
                getSiteSubscription(userId, siteId, true)
            ])
            showNotificationBar(NOTIFICATION_TYPE.SUCCESS, message);
            history.goBack();
        } catch(err) {
            const error = await err;
            console.error(error)
            showNotificationBar(NOTIFICATION_TYPE.ERROR, 'Could not create subscription at this time.');
        } finally {
            setIsLoading(false)
        }
    }

    const handleExtraServiceChange = (price, product, isChecked) => {
        const updatedSelectedExtraServices = { ...fields.selectedExtraServices }
        if (!isChecked) {
            delete updatedSelectedExtraServices[price.id]
        } else {
            updatedSelectedExtraServices[price.id] = { quantity: 1, product, price }
        }

        setFields({ ...fields, selectedExtraServices: updatedSelectedExtraServices })
    }

    const handleAddOnServiceChange = (price, product, isChecked) => {
        const updatedSelectedAddOns = { ...fields.selectedAddOns }
        if (!isChecked) {
            delete updatedSelectedAddOns[price.id]
        } else {
            updatedSelectedAddOns[price.id] = { quantity: 1, product, price }
        }

        setFields({ ...fields, selectedAddOns: updatedSelectedAddOns})
    }

    const handleFieldChange = (key, value) => {
        const updatedFields = { ...fields };
        updatedFields[key] = value;
        if (errors && errors.length > 0) setErrors([]);
        setFields(updatedFields)
    }

    const handleFieldChangeByMap = (updatedFieldMap = {}) => {
        setFields((prevFields) => ({
          ...prevFields,
          ...updatedFieldMap
        }));
      
        if (errors && errors.length > 0) {
          setErrors([]);
        }
    };
    
    const handleAddEmail = () => {
        const updatedEmailsToAdd = [ ...fields.emailsToAdd, emailToAdd]
        const updatedFields = { ...fields, emailsToAdd: updatedEmailsToAdd };
        setFields(updatedFields)
        setEmailToAdd('')
    }

    const renderLoadingContent = () => (
        <StyledProgressCircle />
    )

    const validateOrRemoveImproperPricesForInterval = (interval, fieldsToValidate) => {
        if (!interval || !fieldsToValidate) return fieldsToValidate;
    
        const updatedFields = { ...fieldsToValidate };
    
        if (fieldsToValidate.planPriceSelection) {
            const planId = fieldsToValidate.planSelection;
            const plan = plans.current.find(ep => ep.id === planId);
    
            let planPrice = null;
    
            if (plan && plan.prices) {
                planPrice = plan.prices.find(p => p.id === fieldsToValidate.planPriceSelection);
    
                if (
                    planPrice &&
                    planPrice.recurring &&
                    planPrice.recurring.interval !== interval
                ) {
                    updatedFields.planPriceSelection = undefined;
                }
            }
        }
    
        if (fieldsToValidate.emailHostingPriceId) {
            const price = emailPackages.current.find(ep => ep.id === fieldsToValidate.emailHostingPriceId);
    
            if (
                price &&
                price.recurring &&
                price.recurring.interval !== interval
            ) {
                updatedFields.emailHostingPriceId = undefined;
            }
        }
    
        if (fieldsToValidate.hostingPackagePriceId) {
            const price = hostingPackages.current.find(ep => ep.id === fieldsToValidate.hostingPackagePriceId);
    
            if (
                price &&
                price.recurring &&
                price.recurring.interval !== interval
            ) {
                updatedFields.hostingPackagePriceId = undefined;
            }
        }
    
        if (fieldsToValidate.supportPlanPriceId) {
            const price = supportPlans.current.find(ep => ep.id === fieldsToValidate.supportPlanPriceId);
    
            if (
                price &&
                price.recurring &&
                price.recurring.interval !== interval
            ) {
                updatedFields.supportPlanPriceId = undefined;
            }
        }
    
        const removeImproperPrices = (selectedItems) => {
            const updatedSelectedItems = {};
    
            if (!selectedItems) {
                return updatedSelectedItems;
            }
    
            Object.keys(selectedItems).forEach((key) => {
                const selectedItem = selectedItems[key];
    
                if (
                    selectedItem &&
                    selectedItem.price &&
                    selectedItem.price.recurring &&
                    selectedItem.price.recurring.interval === interval
                ) {
                    updatedSelectedItems[key] = selectedItem;
                }
            });
    
            return updatedSelectedItems;
        };
    
        updatedFields.selectedExtraServices = removeImproperPrices(fieldsToValidate.selectedExtraServices);
        updatedFields.selectedAddOns = removeImproperPrices(fieldsToValidate.selectedAddOns);

        return updatedFields;
    };

    const renderPlansSelection = () => {
        return (
            <Grid container spacing={1} sx={{ marginBottom: '0px' }}>
                <Grid item xs={12}>
                    <StyledTextField
                        select
                        error={errors.includes('planSelection')}
                        value={fields.planSelection}
                        label="Plan Type"
                        disabled={!canModifySubscription()}
                        onChange={(e) => {
                            handleFieldChangeByMap({ planSelection: e.target.value, planPriceSelection: null });
                        }}
                    >
                        <MenuItem key={'NONE'} value={null}>
                            <i>No Plan</i>
                        </MenuItem>
                        {plans.current.map(plan => (
                        <MenuItem key={plan.id} value={plan.id}>
                            {plan.name ? plan.name : 'No Name'}
                        </MenuItem>
                        ))}
                    </StyledTextField>
                </Grid>
            </Grid>
        )
    }


    const renderPlanPricesSelection = () => {
        const plan = plans.current.find(p => p.id === fields.planSelection);

        // ONLY filter out plan prices IF a subscription exists - otherwise interval cannot be changed.
        const options = subscription.current === undefined ? plan.prices : filterPriceForInterval(plan.prices);
        return (
            <Grid container spacing={1} sx={{ marginBottom: '0px' }}>
                <Grid item xs={12}>
                    <StyledTextField
                        select
                        error={errors.includes('planPriceSelection')}
                        value={fields.planPriceSelection}
                        label="Plan Price Structure"
                        disabled={!canModifySubscription()}
                        onChange={(e) => {
                            handleFieldChange('planPriceSelection', e.target.value);
                        }}
                    >
                        {options.map(price => (
                        <MenuItem key={price.id} value={price.id}>
                            {price.nickname ? price.nickname : 'No Name'}: ${(price.unit_amount / 100).toFixed(2)}/{price.recurring.interval || 'unknown'}
                        </MenuItem>
                        ))}
                    </StyledTextField>
                </Grid>
            </Grid>
        )
    }

    const renderPlanHourlyRate = () => {
        return (
            <Grid container spacing={1} sx={{ marginBottom: '0px' }}>
                <Grid item xs={12}>
                    <StyledTextField
                        error={errors.includes('planHourlyRate')}
                        value={fields.planHourlyRate}
                        label="Hourly Rate"
                        type="number"
                        disabled={!canModifySubscription()}
                        inputProps={{
                            min: 0,
                            step: '0.01'
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    $
                                </InputAdornment>
                            )
                        }}
                        onChange={(e) => {
                            handleFieldChange('planHourlyRate', e.target.value);
                        }}
                    />
                </Grid>
            </Grid>
        );
    };

    const renderAddOnServicesContent = () => {
        // const selectedPackage = hostingPackages.current.find(item => item.id === fields.hostingPackagePriceId)
        let filteredProducts = products.current.filter(prod => prod.metadata.group === 'ADDON');
        // if (selectedPackage) {
        //    const includedServices = Object.keys(selectedPackage.package.included_services);
        //    filteredProducts = filteredProducts.filter(prod => !includedServices.includes(prod.metadata.packageServiceType))
        // }
        const selectedServicePriceIds = Object.keys(fields.selectedAddOns);
        const selectedServiceProductIds = Object.values(fields.selectedAddOns).map(p => p.product.id);

        const items = filteredProducts.map(product => ({
            open: selectedServiceProductIds.includes(product.id),
            title: (
            <div style={{ display: 'flex', flexDirection: 'column'}}>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                    {selectedServiceProductIds.includes(product.id) ?
                        <Check style={{ fontSize: '20px', color: Colors.primaryColor, marginRight: '5px' }} /> 
                    :
                        <Add style={{ fontSize: '20px', color: Colors.warningBackground, marginRight: '5px' }} />
                    }
                    <h4 style={{ margin: 0, fontSize: '18px' }}>{product.name}</h4>
                </div>
                <p style={{ marginTop: '0px' }}>{product.description}</p>
            </div>),
            details: <>
                {filterPriceForInterval(product.prices).map((price) => (
                    <div style={{ fontFamily:'Nunito Sans, sans-serif', display: 'flex', flexDirection: 'row' }}>
                        <Checkbox
                            defaultChecked={selectedServicePriceIds.includes(price.id)}
                            onChange={(e) => handleAddOnServiceChange(price, product, e.target.checked)}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column' }}><div>{price.nickname === null ? <i>No Name</i> : <strong>{price.nickname}</strong>}</div><div>${(price.unit_amount / 100).toFixed(2)}/{price.recurring.interval || 'unknown'}</div></div> 
                    </div>
                ))}
            </>
        }));
        return (
            <Grid container spacing={1} sx={{ marginBottom: '24px' }}>
                <Grid item xs={12}>
                    <StyledAccordion items={items} />
                </Grid>
            </Grid>
        )
    }


    const renderHostingPackageContent = () => {
        return (
            <Grid container spacing={1} sx={{ marginBottom: '24px' }}>
                <Grid item xs={12}>
                    <StyledTextField
                        select
                        error={errors.includes('hostingPackagePriceId')}
                        value={fields.hostingPackagePriceId}
                        label="Hosting Type"
                        disabled={!canModifySubscription()}
                        onChange={(e) => handleFieldChange('hostingPackagePriceId', e.target.value)}
                    >
                        <MenuItem key={'NONE'} value={null}>
                            <i>No Hosting</i>
                        </MenuItem>
                        {filterPriceForInterval(hostingPackages.current).map(price => (
                        <MenuItem key={price.id} value={price.id}>
                            {price.nickname ? price.nickname : 'No Name'}: ${(price.unit_amount / 100).toFixed(2)}/{price.recurring.interval || 'unknown'}
                        </MenuItem>
                        ))}
                    </StyledTextField>
                </Grid>
            </Grid>
        )
    }

    const renderSupportPlanContent = () => {
        return (
            <Grid container spacing={1} sx={{ marginBottom: '24px' }}>
                <Grid item xs={12}>
                    <StyledTextField
                        select
                        error={errors.includes('supportPlanPriceId')}
                        value={fields.supportPlanPriceId}
                        disabled={!canModifySubscription()}
                        label="Support Plan"
                        onChange={(e) => handleFieldChange('supportPlanPriceId', e.target.value)}
                    >
                        <MenuItem key={'NONE'} value={null}>
                            <i>No Support Plan</i>
                        </MenuItem>
                        {filterPriceForInterval(supportPlans.current).map(price => (
                        <MenuItem key={price.id} value={price.id}>
                            {price.nickname ? price.nickname : 'No Name'}: ${(price.unit_amount / 100).toFixed(2)}/{price.recurring.interval || 'unknown'}
                        </MenuItem>
                        ))}
                    </StyledTextField>
                </Grid>
            </Grid>
        )
    }

    const renderExtraServicesContent = () => {
        // const selectedPackage = hostingPackages.current.find(item => item.id === fields.hostingPackagePriceId)
        let filteredProducts = products.current.filter(prod => !prod.metadata.group);
        // if (selectedPackage) {
        //    const includedServices = Object.keys(selectedPackage.package.included_services);
        //    filteredProducts = filteredProducts.filter(prod => !includedServices.includes(prod.metadata.packageServiceType))
        // }
        const selectedServicePriceIds = Object.keys(fields.selectedExtraServices);
        const selectedServiceProductIds = Object.values(fields.selectedExtraServices).map(p => p.product.id);

        const items = filteredProducts.map(product => ({
            open: selectedServiceProductIds.includes(product.id),
            title: (
            <div style={{ display: 'flex', flexDirection: 'column'}}>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                    {selectedServiceProductIds.includes(product.id) ?
                        <Check style={{ fontSize: '20px', color: Colors.primaryColor, marginRight: '5px' }} /> 
                    :
                        <Add style={{ fontSize: '20px', color: Colors.warningBackground, marginRight: '5px' }} />
                    }
                    <h4 style={{ margin: 0, fontSize: '18px' }}>{product.name}</h4>
                </div>
                <p style={{ marginTop: '0px' }}>{product.description}</p>
            </div>),
            details: <>
                {filterPriceForInterval(product.prices).map((price) => (
                    <div style={{ fontFamily:'Nunito Sans, sans-serif', display: 'flex', flexDirection: 'row' }}>
                        <Checkbox
                            defaultChecked={selectedServicePriceIds.includes(price.id)}
                            onChange={(e) => handleExtraServiceChange(price, product, e.target.checked)}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column' }}><div>{price.nickname === null ? <i>No Name</i> : <strong>{price.nickname}</strong>}</div><div>${(price.unit_amount / 100).toFixed(2)}/{price.recurring.interval || 'unknown'}</div></div> 
                    </div>
                ))}
            </>
        }));
        return (
            <Grid container spacing={1} sx={{ marginBottom: '24px' }}>
                <Grid item xs={12}>
                    <StyledAccordion items={items} />
                </Grid>
            </Grid>
        )
    }

    const renderOverviewSection = () => {
        const planInterval = getPlanInterval();
        return (
        <Grid container spacing={1} sx={{ marginBottom: '24px' }}>
            <Grid item xs={12}>
                <div style={{marginTop: '30px', marginBottom: '18px' }}><span style={{paddingRight: '25px', ...SUMMARY_TITLE_FONT }}>Renewal Date</span>{getSubscriptionRenewalDate()} (Auto Renewal)</div>
                <Divider />
                <div style={{marginTop: '18px', marginBottom: '18px' }}><span style={{paddingRight: '30px', ...SUMMARY_TITLE_FONT }}>Original Price</span>${(originalFields.current.totalPrice / 100).toFixed(2)}{planInterval ? `/${planInterval.toLowerCase()}` : ''}</div>
                <Divider />
                <div style={{marginTop: '18px', marginBottom: '18px' }}><span style={{paddingRight: '52px', ...SUMMARY_TITLE_FONT }}>New Price</span>${(newTotalPrice / 100).toFixed(2)}{planInterval ? `/${planInterval.toLowerCase()}` : ''}</div>
                <Divider />
            </Grid>
        </Grid>
    )}

    const renderEmailContentSection = () => (
        <Grid container spacing={1} sx={{ marginBottom: '24px' }}>
            <Grid item xs={12}>
                <StyledTextField
                    select
                    error={errors.includes('emailHostingPriceId')}
                    label="Email Hosting Provider"
                    disabled={!canModifySubscription()}
                    value={fields.emailHostingPriceId}
                    onChange={(e) => handleFieldChange('emailHostingPriceId', e.target.value)}
                >
                    <MenuItem key={'none'} value={undefined}>
                        <i>No Email Service</i>
                    </MenuItem>
                    {filterPriceForInterval(emailPackages.current).map((price) => (
                    <MenuItem key={price.id} value={price.id}>
                        {price.nickname ? price.nickname : 'No Name'}: ${(price.unit_amount / 100).toFixed(2)}/mo
                    </MenuItem>
                    ))}
                </StyledTextField>
            </Grid>
            
            {fields.emailHostingPriceId &&
            <>
                <Grid item xs={12}>
                    <List>
                        {fields.emails.map((email) => (
                            <ListItem
                                key={email}
                                dense
                                sx={{ backgroundColor: '#fff', marginBottom: '5px' }}>
                                <ListItemAvatar>
                                    <EmailOutlined sx={{ fontSize: '36px', color: Colors.primaryColor }} />
                                </ListItemAvatar>
                                {fields.emailsToRemove.includes(email) ?
                                    <ListItemText primary={<strike>{email}</strike>} />
                                    : <ListItemText primary={email} />
                                }
                                <ListItemSecondaryAction>
                                {fields.emailsToRemove.includes(email) ? 
                                        <TooltipIconButton
                                            text={'Remove Business Email'}
                                            icon={<UndoOutlined sx={{ fontSize: '24px', color: Colors.primaryColor }} />}
                                            onClick={() => handleFieldChange('emailsToRemove', [ ...fields.emailsToRemove.filter(e => e !== email) ])}
                                        />
                                    :
                                        <TooltipIconButton
                                            text={'Remove Business Email'}
                                            icon={<Delete sx={{ fontSize: '24px', color: Colors.redColor }} />}
                                            onClick={() => handleFieldChange('emailsToRemove', [ ...fields.emailsToRemove, email]) }
                                        />
                                    }
                                   
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                        {fields.emailsToAdd.map((email) => (
                            <ListItem
                                key={email}
                                dense
                                sx={{ backgroundColor: '#fff', marginBottom: '5px' }}>
                                <ListItemAvatar>
                                    <Email sx={{ fontSize: '36px', color: Colors.primaryColor }} />
                                </ListItemAvatar>
                                <ListItemText primary={email} />
                                <ListItemSecondaryAction>
                                    <TooltipIconButton
                                        text={'Remove Business Email'}
                                        icon={<Delete sx={{ fontSize: '24px', color: Colors.redColor }} />}
                                        onClick={() => handleFieldChange('emailsToAdd', [ ...fields.emailsToAdd.filter(e => e !== email) ])}
                                    />
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                    {(!fields.emails || (fields.emails.length === 0 && fields.emailsToAdd.length === 0)) && <p style={{ paddingBottom: '10px' }}>No emails currently being hosted.</p>}
                </Grid>
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', flexDirection:  'row', gap: '10px'}}>
                        <Box sx={{ flexGrow: 1 }}>
                            <StyledTextField
                                label="Business Email"
                                value={emailToAdd}
                                onChange={(e) => setEmailToAdd(e.target.value) }
                                error={errors.includes('emailToAdd')}
                                helperText={errors.includes('emailToAdd') && 'Provide at least one email for this service.'}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                            <ContainedButton
                                onClick={handleAddEmail}
                            >Add</ContainedButton>
                        </Box>
                    </Box>
                </Grid>
            </>
            }
            
        </Grid>
    )

    const canModifySubscription = () => {
        if (stripeDetails.current && stripeDetails.current.payment_method && !stripeDetails.current.payment_method.isValid) return false;
        if (!subscription || !subscription.current || !subscription.current.status) {
            return true; // returning true because no subscription means they can add one
        }
        
        const modifiableStatuses = ['active', 'trialing', 'past_due'];
        return modifiableStatuses.includes(subscription.current.status);
    }

    const checkToRenderBadPaymentMethodWarning = () => {
        if (stripeDetails.current && stripeDetails.current.payment_method && !stripeDetails.current.payment_method.isValid) {
           return (
           <Box sx={{ marginTop: '30px' }}>
               <StyledBanner
                   title={"No Valid Payment Method"}
                   description={`This account does not have a valid payment method attached to it. Details: ${stripeDetails.current.payment_method.reason}`}
                   variant={BANNER_VARIANTS.WARNING}
                   onFix={() => window.open(stripeDetails.current.stripe_billing_portal, "_blank")}
               />    
           </Box>
           )
        }
       }
      

    const checkToRenderSubscriptionWarning = () => {
        if (!subscription || !subscription.current || !subscription.current.status) {
            return undefined; // returning true because no subscription means they can add one
        }
        const modifiableStatuses = ['active', 'trialing', 'past_due'];
        if (!modifiableStatuses.includes(subscription.current.status)) {
            return (
            <Box sx={{ marginTop: '30px' }}>
                <StyledBanner
                    title={"Cannot Edit Subscription"}
                    description={`This subscription has a status of "${subscription.current.status}" and cannot be editted. Remove this subscription from this account and create a new one.`}
                    variant={BANNER_VARIANTS.WARNING}
                />    
            </Box>
            )
        }
    }

    const showIntervalNotice = () => {
        const planInterval = getPlanInterval();
        if (planInterval) {
           return (
           <Box sx={{ marginTop: '10px' }}>
               <StyledBanner
                   title={`Billing By ${planInterval.toUpperCase()}`}
                   description={`This plan is set to be billed ${planInterval}, only prices for this interval can be selected.`}
                   variant={BANNER_VARIANTS.INFO}
               />    
           </Box>
           )
        }
       }

    return (
        <StyledPage>
            <StyledSubHeader title="Edit Plan" closeIcon />
            {isLoading ? renderLoadingContent() :
            <>
                {checkToRenderSubscriptionWarning()}
                {checkToRenderBadPaymentMethodWarning()}
                <StyledSection title="Customization">
                    <h4 style={{ margin: 0, fontSize: '24px' }}>Plan Selection</h4>
                    <p>Choose which plan that will be used to support the client. <strong>Note: The plan recurring interval dictates the entire subscription's recurring schedule.</strong></p>
                    {renderPlansSelection()}
                    {fields.planSelection && renderPlanPricesSelection()}
                    {showIntervalNotice()}

                    <h4 style={{ margin: 0, fontSize: '24px', marginTop: '24px' }}>Business Email</h4>
                    <p>Provide the business emails that the client would like registered and securly managed.</p>
                    {renderEmailContentSection()}
                    <Divider sx={{ marginBottom: '40px;' }} />

                    <h4 style={{ margin: 0, fontSize: '24px' }}>Add-Ons</h4>
                    <p>Select any additional services that the client would like to add to their plan.</p>
                    {renderAddOnServicesContent()}
                </StyledSection>
                <StyledSection title="Hourly Rates">
                    <h4 style={{ margin: 0, fontSize: '24px' }}>Plan Support Rate</h4>
                    <p>Provide the rate at which the client will be billed hourly.</p>
                    {renderPlanHourlyRate()}
                </StyledSection>
                <StyledSection title="Retired Offerings">
                    <h4 style={{ margin: 0, fontSize: '24px' }}>Managed Hosting (LEGACY)</h4>
                    <p>Choose which package that will be used to support the client.</p>
                    {renderHostingPackageContent()}
                    <Divider sx={{ marginBottom: '40px;' }} />

                    <h4 style={{ margin: 0, fontSize: '24px' }}>Support Plan (LEGACY)</h4>
                    <p>Choose which package that will be used to support the client.</p>
                    {renderSupportPlanContent()}
                    <Divider sx={{ marginBottom: '40px;' }} />

                    <h4 style={{ margin: 0, fontSize: '24px' }}>Extra Services (LEGACY)</h4>
                    <p>Select any additional services that the client would like to add to their package.</p>
                    {renderExtraServicesContent()}
                </StyledSection>
                <StyledSection title="Overview">
                    <h4 style={{ margin: 0, fontSize: '24px' }}>Overview</h4>
                    <p>Please confirm the change of price and final price with the client.</p>
                    {renderOverviewSection()}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginTop: '20px',
                        }}
                        >
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {fields.planSelection &&
                                <StyledSwitch
                                title="Notify Client"
                                onChange={(e) => {
                                    setSendNotification(e.target.checked);
                                }}
                                checked={sendNotification}
                                />
                            }

                        </Box>

                        <Box
                            sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            }}
                        >
                            <TextButton onClick={() => history.goBack()} sx={{ marginRight: '10px' }}>
                            Cancel
                            </TextButton>

                            <LoadingButton
                            isLoading={isLoading}
                            disabled={isEqual(fields, originalFields.current) || !canModifySubscription()}
                            onClick={handleSaveSubscription}
                            color="primary"
                            >
                            {subscription.current ? 'Save' : 'Create'}
                            </LoadingButton>
                        </Box>
                        </Box>
                    
                </StyledSection>
            </>
            }
        </StyledPage>
    )
}

export default AdminEditSubscriptionPage;


/*

*/
